import React from "react";
import { isAllBusiness } from "@/api";
import { UsersSettingsTab, UsersSettingsTabs, TabIcons } from "./tabs";
import { useAuth } from "@/Auth";
import { useGetInactiveFoodicsAccounts } from "@/hooks/useGetInactiveFoodicsAccounts";
import { mergeUserEmails } from "@/utils/mergeUserEmails";
import { useGetUsers } from "@/hooks/useGetUsers";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { DropdownOption } from "@/components/Dropdown";
import { UserFilters } from "./UserFilters";
import { UserContent } from "./UserContent";
import type { UserAggregate1 } from "@norma-bi/bi-api";
import { useBusinessesStatusGetter } from "@/hooks/useGetBusinessStatus";
import { OrganizationSyncControl } from "./OrganizationSyncControl";

export function SettingsUsers() {
  const auth = useAuth();
  const userIsDemo = auth.user?.is_demo ?? false;
  const businesses = useGetBusinesses();
  const getBusinessStatus = useBusinessesStatusGetter();

  const [selectedTab, setSelectedTab] = React.useState<UsersSettingsTab>(UsersSettingsTabs.active);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (
      businesses.data?.length &&
      !businesses.data.some((business) => business.id === selectedBusinessId)
    ) {
      setSelectedBusinessId(businesses.data[0].id);
    }
  }, [selectedBusinessId, businesses.data]);

  const usersQuery = useGetUsers({
    businessId: selectedBusinessId,
    userIsDemo,
    enabled: selectedTab === UsersSettingsTabs.active && !!selectedBusinessId,
  });

  const inactiveFoodicsAccountsQuery = useGetInactiveFoodicsAccounts({
    businessId: selectedBusinessId || "",
    userIsDemo,
    enabled:
      selectedTab === UsersSettingsTabs.inactive &&
      !!selectedBusinessId &&
      !isAllBusiness(selectedBusinessId),
  });

  const users = React.useMemo(() => {
    let users: UserAggregate1[] = [];
    if (usersQuery.isSuccess) {
      users = usersQuery.data || [];
    }
    if (searchTerm !== "") {
      users = users.filter((u) =>
        mergeUserEmails(u).toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    return users.map((u) => {
      return {
        id: u.id,
        name: u.name,
        email: mergeUserEmails(u),
        branchesCount: u.venues_count,
        businessesCount: u.businesses_count,
        is_root: u.is_root,
        web_app_privileges: u.meta?.norma?.web_app_privileges,
      };
    });
  }, [usersQuery.isSuccess, usersQuery.data, searchTerm]);

  const inactiveFoodicsAccounts = React.useMemo(() => {
    if (!inactiveFoodicsAccountsQuery.isSuccess) return [];

    const acc = inactiveFoodicsAccountsQuery.data.filter((a) => {
      return a.email.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const mappedAccounts = acc.map((a) => {
      return {
        id: a.id,
        name: a.name,
        email: a.email,
        branchesCount: a.branches?.length ?? 0,
        roles: [...(a.is_owner ? ["Owner"] : []), ...(a.roles?.map((r) => r.name) ?? [])],
      };
    });

    return mappedAccounts.sort((a, b) => {
      const countA = a.branchesCount || 0;
      const countB = b.branchesCount || 0;
      return countB - countA;
    });
  }, [inactiveFoodicsAccountsQuery.isSuccess, inactiveFoodicsAccountsQuery.data, searchTerm]);

  const tabs = React.useMemo(() => [UsersSettingsTabs.active, UsersSettingsTabs.inactive], []);

  const statusOptions = React.useMemo<DropdownOption<UsersSettingsTab>[]>(() => {
    return tabs.map((tab) => ({
      id: tab,
      label: tab,
      icon: React.createElement(TabIcons[tab]),
    }));
  }, [tabs]);

  const businessOptions = React.useMemo<DropdownOption<string>[]>(() => {
    if (!businesses.isSuccess) return [];

    return businesses.data.map((business) => {
      const status = getBusinessStatus(business);
      return {
        id: business.id,
        label: business.display_name,
        disabled: status === "unlicensed" || status === "onboarding",
        metadata:
          status === "onboarding" ? (
            <span className="text-xs text-feintGray font-normal">Pending</span>
          ) : status === "unlicensed" ? (
            <span className="text-xs text-feintGray font-normal">Unlicensed</span>
          ) : undefined,
      };
    });
  }, [businesses.isSuccess, businesses.data, getBusinessStatus]);

  function onSelectBusiness(businessId: string) {
    setSelectedBusinessId(businessId);
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xl font-medium text-feintGray">Users</div>
            <p className="text-sm text-feintGray">
              {selectedTab === UsersSettingsTabs.active
                ? "People with BI accounts"
                : "Foodics accounts that can receive login instructions"}
            </p>
          </div>
          <OrganizationSyncControl
            businessId={selectedBusinessId}
            userIsDemo={userIsDemo}
            inactiveTab={selectedTab === UsersSettingsTabs.inactive}
          />
        </div>
        <UserFilters
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          statusOptions={statusOptions}
          businessOptions={businessOptions}
          selectedBusinessId={selectedBusinessId}
          onSelectBusiness={onSelectBusiness}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
        />
      </div>
      <div className="pt-8 flex-1 min-h-0 overflow-hidden">
        <UserContent
          selectedTab={selectedTab}
          businessId={selectedBusinessId}
          users={users}
          inactiveFoodicsAccounts={inactiveFoodicsAccounts}
          usersQuery={usersQuery}
          inactiveFoodicsAccountsQuery={inactiveFoodicsAccountsQuery}
        />
      </div>
    </div>
  );
}
